import { isConfigEnabled } from './isConfigEnabled';

const DEFAULT_DATE_VALIDATION_RULES = [
    {
        id: 'target-tier0-before-tier1',
        isEnable: true,
        earlierField: 'tier0TargetReviewDate',
        laterField: 'tier1TargetReviewDate',
        message: 'Tier 0 target review date must be before Tier 1 target review date.',
    },
    {
        id: 'target-tier1-before-tier2',
        isEnable: true,
        earlierField: 'tier1TargetReviewDate',
        laterField: 'tier2TargetReviewDate',
        message: 'Tier 1 target review date must be before Tier 2 target review date.',
    },
    {
        id: 'scheduled-tier0-before-tier1',
        isEnable: true,
        earlierField: 'tier0RequestedReviewDate',
        laterField: 'tier1RequestedReviewDate',
        message: 'Tier 0 scheduled date must be before Tier 1 scheduled date.',
    },
    {
        id: 'scheduled-tier1-before-tier2',
        isEnable: true,
        earlierField: 'tier1RequestedReviewDate',
        laterField: 'tier2RequestedReviewDate',
        message: 'Tier 1 scheduled date must be before Tier 2 scheduled date.',
    },
];

const DEFAULT_TIER_RESET_RULES = [
    {
        id: 'reset-tier-0',
        isEnable: true,
        tier: 'Tier 0',
        showButtonWhen: [
            { field: 'tier0TargetReviewDate', operator: 'hasValue' },
            { field: 'tier0Decision', operator: 'anyOf', value: ['Approved With Actions', 'Approved'] },
        ],
        resetFields: ['tier0TargetReviewDate', 'tier0Decision'],
    },
    {
        id: 'reset-tier-1',
        isEnable: true,
        tier: 'Tier 1',
        showButtonWhen: [
            { field: 'tier1TargetReviewDate', operator: 'hasValue' },
            { field: 'tier1Decision', operator: 'anyOf', value: ['Approved With Actions', 'Approved'] },
        ],
        resetFields: ['tier0TargetReviewDate', 'tier0Decision', 'tier1TargetReviewDate', 'tier1Decision'],
    },
    {
        id: 'reset-tier-2',
        isEnable: true,
        tier: 'Tier 2',
        showButtonWhen: [
            { field: 'tier2TargetReviewDate', operator: 'hasValue' },
            { field: 'tier2Decision', operator: 'anyOf', value: ['Approved With Actions', 'Approved'] },
        ],
        resetFields: [
            'tier0TargetReviewDate',
            'tier0Decision',
            'tier1TargetReviewDate',
            'tier1Decision',
            'tier2TargetReviewDate',
            'tier2Decision',
        ],
    },
];

/**
 * Maps new-style condition operators to legacy tier-reset operator names.
 */
const mapResetOperator = (operator) => {
    if (operator === 'hasValue') {
        return 'is_there';
    }

    if (operator === 'anyOf') {
        return 'any_of';
    }

    if (operator === 'equals') {
        return 'is_equals';
    }

    return operator;
};

/**
 * Converts behavior.tier.reset.rules into legacy TIER_RESET_CONFIG shape.
 */
const convertTierResetRulesToLegacy = (tierConfig = {}) => {
    const controlField = tierConfig.controlField || 'topTierRequired';
    const rules = tierConfig.reset?.rules || [];

    return rules
        .filter((rule) => isConfigEnabled(rule))
        .map((rule) => ({
            selectedConditionDataElement: controlField,
            selectedConditionOperator: 'is_equals',
            value: rule.tier,
            actionButtonShowFields: (rule.showButtonWhen || []).map((condition) => ({
                selectedConditionDataElement: condition.field,
                selectedConditionOperator: mapResetOperator(condition.operator),
                value: condition.value,
            })),
            onClickResetButtonResetFields: rule.resetFields || [],
        }));
};

/**
 * Resolves legacy tier-reset config from chartData root keys.
 */
const resolveLegacyTierResetConfig = (chartData = {}) => {
    const candidates = [
        chartData.tierResetConfig,
        chartData.TIER_RESET_CONFIG,
        chartData.tabs?.dealReviewDetails?.TIER_RESET_CONFIG,
    ];

    return candidates.find((candidate) => Array.isArray(candidate) && candidate.length) || [];
};

/**
 * Deep-merges behavior config with defaults and resolves legacy chartData keys.
 */
export const resolveBehaviorConfig = (chartData = {}) => {
    const rawBehavior = chartData.behavior || {};
    const tierRaw = rawBehavior.tier || {};
    const hasBehaviorBlock = Boolean(chartData.behavior);
    const behaviorMasterEnabled = isConfigEnabled(rawBehavior, true);
    const legacyIdmEnabled = chartData.isIDMEnable === true;
    const tierDefaultEnabled = hasBehaviorBlock ? behaviorMasterEnabled : true;

    const tierConfig = {
        isEnable: isConfigEnabled(tierRaw, tierDefaultEnabled),
        controlField: tierRaw.controlField || 'topTierRequired',
        controlFieldReadOnly: tierRaw.controlFieldReadOnly === true,
        namePattern: tierRaw.namePattern || 'Tier {n}',
        maxTierLevel: tierRaw.maxTierLevel ?? 2,
        editability: {
            isEnable: isConfigEnabled(tierRaw.editability, tierDefaultEnabled),
            strategy: tierRaw.editability?.strategy || 'lteTierMatch',
            controlFieldEditable: tierRaw.editability?.controlFieldEditable !== false,
            fieldTierDetection: {
                isEnable: isConfigEnabled(tierRaw.editability?.fieldTierDetection, true),
                source: tierRaw.editability?.fieldTierDetection?.source || 'fieldNamePattern',
                pattern: tierRaw.editability?.fieldTierDetection?.pattern || 'tier{n}',
                tierLevels: tierRaw.editability?.fieldTierDetection?.tierLevels || [0, 1, 2],
            },
            messages: {
                tier0BlocksHigher: tierRaw.editability?.messages?.tier0BlocksHigher
                    || 'You cannot modify Tier 1 and Tier 2 fields because Tier Required is Tier 0.',
                tier1BlocksTier2: tierRaw.editability?.messages?.tier1BlocksTier2
                    || 'You cannot modify Tier 2 fields because Tier Required is Tier 1.',
                generic: tierRaw.editability?.messages?.generic
                    || 'You cannot modify this field for the current Tier Required value.',
            },
        },
        clearOnChange: {
            isEnable: isConfigEnabled(tierRaw.clearOnChange, true),
            strategy: tierRaw.clearOnChange?.strategy || 'clearHigherTiers',
            clearHistoryFields: tierRaw.clearOnChange?.clearHistoryFields === true,
            historyFieldSuffix: tierRaw.clearOnChange?.historyFieldSuffix || 'History',
            triggerOn: {
                controlFieldSave: tierRaw.clearOnChange?.triggerOn?.controlFieldSave !== false,
                autoCalculationFields: tierRaw.clearOnChange?.triggerOn?.autoCalculationFields || [],
            },
        },
        dateValidation: {
            isEnable: isConfigEnabled(tierRaw.dateValidation, true),
            mode: tierRaw.dateValidation?.mode === 'error' ? 'error' : 'warning',
            feedbackType: tierRaw.dateValidation?.feedbackType || 'inline',
            rules: tierRaw.dateValidation?.rules?.length
                ? tierRaw.dateValidation.rules
                : DEFAULT_DATE_VALIDATION_RULES,
        },
        reset: {
            isEnable: isConfigEnabled(tierRaw.reset, true),
            successMessage: tierRaw.reset?.successMessage || 'Deal Reset Completed',
            errorMessage: tierRaw.reset?.errorMessage || 'Reset failed',
            notConfiguredMessage: tierRaw.reset?.notConfiguredMessage
                || 'Tier reset is not configured for this list.',
            unsavedChangesMessage: tierRaw.reset?.unsavedChangesMessage
                || 'Save your changes first before reset document',
            rules: tierRaw.reset?.rules?.length
                ? tierRaw.reset.rules
                : DEFAULT_TIER_RESET_RULES,
        },
    };

    const negotiationRaw = rawBehavior.negotiationStatus || {};
    const negotiationStatus = {
        isEnable: isConfigEnabled(negotiationRaw, behaviorMasterEnabled),
        field: negotiationRaw.field || 'negotiationStatus',
        approvalDateField: negotiationRaw.approvalDateField || 'approvalDate',
        topTierField: negotiationRaw.topTierField || tierConfig.controlField,
        options: negotiationRaw.options || [
            'Deal Review',
            'New Negotiation',
            'Negotiation In Progress',
            'Negotiation Completed',
            'Negotiation Cancelled',
        ],
        autoUpdate: {
            isEnable: isConfigEnabled(negotiationRaw.autoUpdate, behaviorMasterEnabled),
            skipWhenCurrentValueIn: negotiationRaw.autoUpdate?.skipWhenCurrentValueIn || ['Negotiation Cancelled'],
            skipWhenSavingField: negotiationRaw.autoUpdate?.skipWhenSavingField || 'negotiationStatus',
            tierApprovalDateFields: negotiationRaw.autoUpdate?.tierApprovalDateFields || {
                0: 'tier0ApprovalDate',
                1: 'tier1ApprovalDate',
                2: 'tier2ApprovalDate',
            },
            triggerFields: {
                always: negotiationRaw.autoUpdate?.triggerFields?.always || ['approvalDate', 'topTierRequired'],
                includeRelevantTierApprovalDate: negotiationRaw.autoUpdate?.triggerFields?.includeRelevantTierApprovalDate !== false,
            },
            rules: negotiationRaw.autoUpdate?.rules || [
                {
                    id: 'completed',
                    isEnable: true,
                    priority: 1,
                    condition: { field: 'approvalDate', operator: 'hasValue' },
                    set: 'Negotiation Completed',
                },
                {
                    id: 'in-progress',
                    isEnable: true,
                    priority: 2,
                    condition: { type: 'relevantTierApprovalDate', operator: 'hasValue' },
                    set: 'Negotiation In Progress',
                },
                {
                    id: 'default',
                    isEnable: true,
                    priority: 99,
                    condition: { operator: 'default' },
                    set: 'Deal Review',
                },
            ],
        },
        manualValidation: {
            isEnable: isConfigEnabled(negotiationRaw.manualValidation, false),
            feedbackType: negotiationRaw.manualValidation?.feedbackType || 'toast',
            blockSave: negotiationRaw.manualValidation?.blockSave !== false,
            rules: negotiationRaw.manualValidation?.rules || [],
        },
    };

    const featuresRaw = rawBehavior.features || {};
    const tableRaw = rawBehavior.table || {};
    const loaderRaw = tableRaw.loader || {};
    const loaderShowOn = loaderRaw.showOn || {};
    const saveFeedbackRaw = tableRaw.saveFeedback || {};
    const apiRaw = rawBehavior.api || {};
    const columnsRaw = rawBehavior.columns || {};
    const paginationRaw = tableRaw.pagination || chartData.pagination || {};

    const tierResetLegacy = tierConfig.reset.rules.length
        ? convertTierResetRulesToLegacy(tierConfig)
        : resolveLegacyTierResetConfig(chartData);

    return {
        isEnable: isConfigEnabled(rawBehavior, true),
        features: {
            inlineEdit: { isEnable: isConfigEnabled(featuresRaw.inlineEdit, true) },
            rowSelection: { isEnable: isConfigEnabled(featuresRaw.rowSelection, Boolean(chartData?.checkbox)) },
            fieldHistory: { isEnable: isConfigEnabled(featuresRaw.fieldHistory, true) },
            externalFilter: { isEnable: isConfigEnabled(featuresRaw.externalFilter, true) },
            columnSorting: { isEnable: isConfigEnabled(featuresRaw.columnSorting, true) },
            pageTitle: { isEnable: isConfigEnabled(featuresRaw.pageTitle, true) },
            silentRefreshOnSave: { isEnable: isConfigEnabled(featuresRaw.silentRefreshOnSave, true) },
            optimisticRowUpdateOnSave: { isEnable: isConfigEnabled(featuresRaw.optimisticRowUpdateOnSave, true) },
        },
        api: {
            isEnable: isConfigEnabled(apiRaw, true),
            idm: { isEnable: isConfigEnabled(apiRaw.idm, legacyIdmEnabled) },
            historyDataModelId: apiRaw.historyDataModelId
                || chartData.historyDataModelId
                || chartData.tabs?.activities?.dataModelId
                || chartData.selectedDataModel,
            subDataModelId: apiRaw.subDataModelId
                || chartData.subDataModelId
                || chartData.historySubDataModelId
                || chartData.tabs?.activities?.subDataModelId
                || chartData.selectedDataModel,
            alwaysIncludeFields: apiRaw.alwaysIncludeFields || [
                '_id',
                'topTierRequired',
                'negotiationStatus',
                'approvalDate',
                'tier0ApprovalDate',
                'tier1ApprovalDate',
                'tier2ApprovalDate',
                'updatedDate',
            ],
        },
        table: {
            isEnable: isConfigEnabled(tableRaw, true),
            minColumnWidth: tableRaw.minColumnWidth || 200,
            emptyMessage: tableRaw.emptyMessage || 'No data available',
            pagination: {
                isEnable: isConfigEnabled(paginationRaw, true),
                useChartPagination: paginationRaw.useChartPagination !== false,
                rowsPerPageOptions: paginationRaw.rowsPerPageOptions || [10, 15, 25, 50, 100],
                labelRowsPerPage: paginationRaw.labelRowsPerPage || 'Rows per page:',
            },
            editCommit: {
                isEnable: isConfigEnabled(tableRaw.editCommit, true),
                textOnBlur: tableRaw.editCommit?.textOnBlur !== false,
                dropdownOnChange: tableRaw.editCommit?.dropdownOnChange !== false,
                apiSelectOnChange: tableRaw.editCommit?.apiSelectOnChange !== false,
                checkboxOnChange: tableRaw.editCommit?.checkboxOnChange !== false,
            },
            loader: {
                isEnable: isConfigEnabled(loaderRaw, true),
                showOn: {
                    initialLoad: loaderShowOn.initialLoad !== false,
                    filterChange: loaderShowOn.filterChange !== false,
                    pagination: loaderShowOn.pagination !== false,
                    sortChange: loaderShowOn.sortChange !== false,
                    rowsPerPageChange: loaderShowOn.rowsPerPageChange !== false,
                    cellSave: loaderShowOn.cellSave === true,
                    tierReset: loaderShowOn.tierReset === true,
                },
            },
            saveFeedback: {
                isEnable: isConfigEnabled(saveFeedbackRaw, true),
                successToast: {
                    isEnable: isConfigEnabled(saveFeedbackRaw.successToast, true),
                    useApiMessage: saveFeedbackRaw.successToast?.useApiMessage !== false,
                    message: saveFeedbackRaw.successToast?.message || 'Record updated successfully',
                },
                errorToast: {
                    isEnable: isConfigEnabled(saveFeedbackRaw.errorToast, true),
                    message: saveFeedbackRaw.errorToast?.message || 'Failed to update record',
                },
            },
        },
        tier: tierConfig,
        negotiationStatus,
        validation: {
            isEnable: isConfigEnabled(rawBehavior.validation, true),
            required: {
                isEnable: isConfigEnabled(rawBehavior.validation?.required, true),
                feedbackType: rawBehavior.validation?.required?.feedbackType || 'toast',
                messageTemplate: rawBehavior.validation?.required?.messageTemplate
                    || '{fieldLabel} is required.',
            },
            nonEditable: {
                isEnable: isConfigEnabled(rawBehavior.validation?.nonEditable, true),
                feedbackType: rawBehavior.validation?.nonEditable?.feedbackType || 'toast',
                message: rawBehavior.validation?.nonEditable?.message
                    || 'You cannot modify {fieldLabel} because it is not editable.',
            },
            selectClearValue: {
                isEnable: isConfigEnabled(rawBehavior.validation?.selectClearValue, true),
                emptyAsNull: rawBehavior.validation?.selectClearValue?.emptyAsNull !== false,
            },
        },
        columns: {
            isEnable: isConfigEnabled(columnsRaw, true),
            hidden: {
                isEnable: isConfigEnabled(columnsRaw.hidden, true),
                flagKey: columnsRaw.hidden?.flagKey || 'isHide',
            },
            conditions: {
                isEnable: isConfigEnabled(columnsRaw.conditions, true),
                flagKey: columnsRaw.conditions?.flagKey || 'conditions',
            },
            sortable: {
                isEnable: isConfigEnabled(columnsRaw.sortable, true),
                defaultSortable: columnsRaw.sortable?.defaultSortable !== false,
                disableFlagKey: columnsRaw.sortable?.disableFlagKey || 'sort',
            },
            history: {
                isEnable: isConfigEnabled(columnsRaw.history, true),
                requiredFlags: columnsRaw.history?.requiredFlags || {
                    isHistory: true,
                    showHistroy: true,
                },
                historyKeySuffix: columnsRaw.history?.historyKeySuffix || 'History',
                tooltip: columnsRaw.history?.tooltip || 'View field history',
            },
            tierResetIcon: {
                isEnable: isConfigEnabled(columnsRaw.tierResetIcon, true),
                flagKey: columnsRaw.tierResetIcon?.flagKey || 'showTierReset',
                tooltip: columnsRaw.tierResetIcon?.tooltip || 'Reset tier data',
            },
            display: {
                isEnable: isConfigEnabled(columnsRaw.display, true),
                ellipsis: columnsRaw.display?.ellipsis !== false,
                tooltipOnHover: columnsRaw.display?.tooltipOnHover !== false,
                emptyPlaceholder: columnsRaw.display?.emptyPlaceholder || '—',
            },
        },
        tierResetLegacy: isConfigEnabled(tierConfig.reset) ? tierResetLegacy : [],
    };
};

/**
 * Returns whether IDM API mode is active from resolved behavior config.
 */
export const isIdmApiEnabled = (behaviorConfig) => (
    isConfigEnabled(behaviorConfig)
    && isConfigEnabled(behaviorConfig.api)
    && isConfigEnabled(behaviorConfig.api.idm)
);

/**
 * Resolves loader visibility for a table fetch trigger.
 */
export const shouldShowTableLoader = (behaviorConfig, trigger) => {
    if (!isConfigEnabled(behaviorConfig?.table) || !isConfigEnabled(behaviorConfig?.table?.loader)) {
        return true;
    }

    return behaviorConfig.table.loader.showOn[trigger] !== false;
};
